import { useCastersContext } from "@/pages/caster/caster-data-entry-v2/CastersContext";
import { useSaveProbe } from "@/pages/caster/caster-data-entry-v2/hooks/useSaveProbe";
import { useSaveProcess } from "@/pages/caster/caster-data-entry-v2/hooks/useSaveProcess";
import { useModalStyles } from "@/pages/caster/styles/modalStyles";
import { validateField } from "@/pages/eaf/data-entry/components/tables/utils/validateMinMax";
import {
	ButtonWithLoading,
	ErrorModal,
	HelmetDexteel,
	MesfModal,
	getMomentTz,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Button, ButtonProps, Grid2 as Grid } from "@mui/material";
import { get } from "lodash-es";
import React, { useEffect, useState, useMemo } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { DateTimePickerDE } from "../../../form-fields/common/DateTimePickerDE";
import { NumberTextFieldDE } from "../../../form-fields/common/NumberTextFieldDE";
import { IProbeRow } from "../hooks/useProbesTableData";

interface UpsertDeleteProbeModalProps {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	mode: "create" | "edit" | "delete";
	row: IProbeRow | null;
	limitDates?: {
		start: Date;
		end: Date | null;
	};
}

type FormValues = {
	Timestamp: Date;
	Temperature: number;
	TrimProbeOxygen: number;
};

const moment = getMomentTz();
export const UpsertDeleteProbeModal: React.FC<UpsertDeleteProbeModalProps> = ({
	show,
	onHide,
	mode,
	row,
	limitDates,
}) => {
	const { state } = useCastersContext();
	const {
		state: { settings },
	} = useUTLSettingsContext();
	const MIN_TEMP_MANUAL = get(settings, "MAX_TEMP_PROBE", 2500);
	const MAX_TEMP_MANUAL = get(settings, "MIN_TEMP_PROBE", 3500);

	const classes = useModalStyles();

	const { saveProbe, removeProbe } = useSaveProbe();
	const { saveTrimData } = useSaveProcess();

	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [localError, setLocalError] = useState("");

	const defaultTimestamp = useMemo(() => {
		if (mode === "create") {
			return state.processData?.LadleOpen
				? new Date(state.processData.LadleOpen)
				: new Date();
		} else {
			return row && row.timestamp ? new Date(row.timestamp) : new Date();
		}
	}, [mode, state.processData?.LadleOpen, row?.timestamp]);

	const defaultTemperature = useMemo(() => {
		return mode === "create" ? 0 : row ? row.temperature || 0 : 0;
	}, [mode, row?.temperature]);

	const defaultTrimProbeOxygen = useMemo(() => {
		return mode === "create" ? 0 : row ? row.trimProbeOxygen || 0 : 0;
	}, [mode, row?.trimProbeOxygen]);

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<FormValues>({
		defaultValues: {
			Timestamp: defaultTimestamp,
			Temperature: defaultTemperature,
			TrimProbeOxygen: defaultTrimProbeOxygen,
		},
	});

	useEffect(() => {
		if (show) {
			reset({
				Timestamp: defaultTimestamp,
				Temperature: defaultTemperature,
				TrimProbeOxygen: defaultTrimProbeOxygen,
			});
		}
	}, [
		show,
		reset,
		defaultTimestamp,
		defaultTemperature,
		defaultTrimProbeOxygen,
	]);

	const onSubmit: SubmitHandler<FormValues> = async (data) => {
		setIsSubmitLoading(true);
		try {
			if (mode === "delete") {
				await removeProbe(Number(row!.id));
			} else {
				if (row && row.id === "ladle") {
					await saveTrimData("TrimProbeTime", moment.utc(data.Timestamp));
					await saveTrimData("TrimProbeTemperature", data.Temperature);
					await saveTrimData("TrimProbeOxygen", Number(data.TrimProbeOxygen));
				} else {
					const probeId = mode === "create" ? 0 : Number(row!.id);

					await saveProbe({
						ProbeId: probeId,
						Timestamp: data.Timestamp,
						Temperature: Number(data.Temperature),
						Oxygen: null,
						ProcessId: state.processData?.ProcessId || 0,
						IsProbeManual: true,
					});
				}
			}
			setIsSubmitLoading(false);
			onHide(true);
		} catch (err: any) {
			setLocalError(
				err.message ||
					`Error ${mode === "delete" ? "deleting" : "saving"} probe`,
			);
			setIsSubmitLoading(false);
		}
	};

	const temperatureDisabled: boolean = mode === "delete";
	const title: string = `${mode.toUpperCase()} PROBE`;
	const isDisabled: boolean = mode === "delete";
	const buttonLabel: string = mode === "delete" ? "Delete" : "Save";
	const buttonColor: ButtonProps["color"] =
		mode === "delete" ? "error" : "primary";

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`${mode[0].toUpperCase() + mode.slice(1)} Probe`}
				/>
			)}
			<MesfModal
				title={title}
				open={show}
				handleClose={() => onHide(false)}
				id="upsert-delete-probe-modal"
				maxWidth="sm"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
						<Grid container spacing={2}>
							<Grid size={{ xs: row && row.id === "ladle" ? 4 : 6 }}>
								<Controller
									name="Timestamp"
									control={control}
									rules={{
										required: "Timestamp is required",
										validate: (value) => {
											if (row && row.id === "ladle") {
												if (limitDates?.start && value >= limitDates.start) {
													return "Ladle probe timestamp must be before the process start";
												}
												return true;
											}

											if (limitDates?.start && value < limitDates.start) {
												return "Timestamp must be between the start and end of the process";
											} else if (limitDates?.end && value > limitDates.end) {
												return "Timestamp must be less than the end of the process";
											} else {
												return true;
											}
										},
									}}
									render={({ field }) => (
										<DateTimePickerDE
											value={field.value}
											format="HH:mm - MM/DD/YYYY"
											label="Time"
											onChange={(date) => field.onChange(date)}
											onBlur={() => {}}
											error={errors.Timestamp ? errors.Timestamp.message : ""}
											pause={() => {}}
											resume={() => {}}
											disabled={isDisabled}
											disableOpenPicker
										/>
									)}
								/>
							</Grid>
							<Grid size={{ xs: row && row.id === "ladle" ? 4 : 6 }}>
								<Controller
									name="Temperature"
									control={control}
									rules={{
										required: "Temperature is required",
										validate: (value) =>
											validateField(
												value,
												MIN_TEMP_MANUAL,
												MAX_TEMP_MANUAL,
												"Temperature",
											),
									}}
									render={({ field }) => (
										<NumberTextFieldDE
											{...field}
											value={parseInt(field.value?.toFixed(0))}
											onChange={field.onChange}
											onBlur={() => {}}
											label="Temperature (F)"
											disabled={temperatureDisabled}
											pause={() => {}}
											resume={() => {}}
											error={
												errors.Temperature ? errors.Temperature.message : ""
											}
										/>
									)}
								/>
							</Grid>
							{row && row.id === "ladle" && (
								<Grid size={{ xs: 4 }}>
									<Controller
										name="TrimProbeOxygen"
										control={control}
										render={({ field }) => (
											<NumberTextFieldDE
												{...field}
												value={parseInt(field.value?.toFixed(0))}
												onChange={field.onChange}
												onBlur={() => {}}
												label="Trim O2"
												pause={() => {}}
												resume={() => {}}
												error={
													errors.TrimProbeOxygen
														? errors.TrimProbeOxygen.message
														: ""
												}
											/>
										)}
									/>
								</Grid>
							)}
						</Grid>
						{localError && (
							<div className={classes.errorLabel} style={{ marginTop: "10px" }}>
								{localError}
							</div>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px" }}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid size={{ xs: 12, md: 6 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<ButtonWithLoading
									fullWidth
									isLoading={isSubmitLoading}
									variant="contained"
									color={buttonColor}
									type="submit"
								>
									{buttonLabel}
								</ButtonWithLoading>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal
				error={localError}
				onHide={() => setLocalError("")}
				title={`Error ${mode === "delete" ? "Deleting" : "Saving"} Probe`}
			/>
		</>
	);
};
